/**
 * Tool Create/Edit Dialog Component
 * Modal form for adding or editing tools
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { ToolPhotoUploader } from './ToolPhotoUploader';
import { useCreateTool, useUpdateTool, useUploadToolPhoto } from '@/hooks/useToolbox';
import {
  type ToolboxCategory,
  type TechnicianToolWithMedia,
  type ParsedToolData,
  TOOLBOX_CATEGORY_LABELS,
  TOOLBOX_CATEGORY_ORDER,
} from '@/types/toolbox';

interface ToolCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  technicianProfileId: string;
  editingTool?: TechnicianToolWithMedia | null;
}

export function ToolCreateDialog({
  open,
  onOpenChange,
  technicianProfileId,
  editingTool,
}: ToolCreateDialogProps) {
  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState<ToolboxCategory>('other');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  // Mutations
  const createTool = useCreateTool();
  const updateTool = useUpdateTool();
  const uploadPhoto = useUploadToolPhoto();

  const isEditing = !!editingTool;
  const isSubmitting = createTool.isPending || updateTool.isPending || uploadPhoto.isPending;

  // Populate form when editing
  useEffect(() => {
    if (editingTool) {
      setName(editingTool.name);
      setCategory(editingTool.category);
      setBrand(editingTool.brand || '');
      setModel(editingTool.model || '');
      setPartNumber(editingTool.part_number || '');
      setSerialNumber(editingTool.serial_number || '');
      setPurchasePrice(editingTool.purchase_price?.toString() || '');
      setPurchaseDate(editingTool.purchase_date || '');
      setNotes(editingTool.notes || '');
      setPhotoFile(null);
    } else {
      resetForm();
    }
  }, [editingTool, open]);

  const resetForm = () => {
    setName('');
    setCategory('other');
    setBrand('');
    setModel('');
    setPartNumber('');
    setSerialNumber('');
    setPurchasePrice('');
    setPurchaseDate('');
    setNotes('');
    setPhotoFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    try {
      const priceNum = purchasePrice ? parseFloat(purchasePrice) : null;

      if (isEditing && editingTool) {
        // Update existing tool
        await updateTool.mutateAsync({
          id: editingTool.id,
          name: name.trim(),
          category,
          brand: brand.trim() || null,
          model: model.trim() || null,
          part_number: partNumber.trim() || null,
          serial_number: serialNumber.trim() || null,
          purchase_price: priceNum,
          purchase_date: purchaseDate || null,
          notes: notes.trim() || null,
        });

        // Upload new photo if provided
        if (photoFile) {
          await uploadPhoto.mutateAsync({
            toolId: editingTool.id,
            technicianProfileId,
            file: photoFile,
            isPrimary: true,
          });
        }
      } else {
        // Create new tool
        const tool = await createTool.mutateAsync({
          technician_profile_id: technicianProfileId,
          name: name.trim(),
          category,
          brand: brand.trim() || null,
          model: model.trim() || null,
          part_number: partNumber.trim() || null,
          serial_number: serialNumber.trim() || null,
          purchase_price: priceNum,
          purchase_date: purchaseDate || null,
          notes: notes.trim() || null,
        });

        // Upload photo if provided
        if (photoFile) {
          await uploadPhoto.mutateAsync({
            toolId: tool.id,
            technicianProfileId,
            file: photoFile,
            isPrimary: true,
          });
        }
      }

      onOpenChange(false);
      resetForm();
    } catch (error) {
      // Error handling is done in the mutation hooks
      console.error('Failed to save tool:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Tool' : 'Add Tool'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Photo Upload */}
          <div>
            <Label className="mb-2 block">Photo</Label>
            <ToolPhotoUploader
              value={photoFile}
              previewUrl={editingTool?.primary_photo_url}
              onChange={setPhotoFile}
              onAnalysisComplete={(data: ParsedToolData) => {
                // Only fill empty fields - don't overwrite user edits
                if (!name.trim() && data.name) setName(data.name);
                if (!brand.trim() && data.brand) setBrand(data.brand);
                if (!model.trim() && data.model) setModel(data.model);
                if (!partNumber.trim() && data.part_number) setPartNumber(data.part_number);
                if (!serialNumber.trim() && data.serial_number) setSerialNumber(data.serial_number);
                if (category === 'other' && data.category) setCategory(data.category);
              }}
              disabled={isSubmitting}
            />
          </div>

          {/* Tool Name */}
          <div>
            <Label htmlFor="name">Tool Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Snap-on ZEUS+"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Category */}
          <div>
            <Label htmlFor="category">Category *</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as ToolboxCategory)}>
              <SelectTrigger id="category" disabled={isSubmitting}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TOOLBOX_CATEGORY_ORDER.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {TOOLBOX_CATEGORY_LABELS[cat]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Brand & Model Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="brand">Brand</Label>
              <Input
                id="brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="e.g., Snap-on"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <Label htmlFor="model">Model</Label>
              <Input
                id="model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="e.g., ZEUS+"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Part Number & Serial Number Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="partNumber">Part Number</Label>
              <Input
                id="partNumber"
                value={partNumber}
                onChange={(e) => setPartNumber(e.target.value)}
                placeholder="Manufacturer P/N"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <Label htmlFor="serialNumber">Serial Number</Label>
              <Input
                id="serialNumber"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="For warranty/theft"
                className="font-mono text-sm"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Purchase Info Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="purchasePrice">Purchase Price</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="purchasePrice"
                  type="number"
                  step="0.01"
                  min="0"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  placeholder="0.00"
                  className="pl-7"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="purchaseDate">Purchase Date</Label>
              <Input
                id="purchaseDate"
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              rows={2}
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Add Tool'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
